import _ from 'lodash';
import { PAGE_TYPE } from '../../features/base/app/constants.ts';
import { getLocalIDMember } from '../../features/base/members/index.ts';
import { getPropertyValue } from '../../features/base/settings/functions.ts';
import { leaveRoom, setRoomJoined } from '../../features/room';
import { getLocalSharingStatus } from '../../features/screen-share';
import { INIT_VISIBLE, VIDEO_MODE_LIST, setLayoutMode, videoLayoutMode } from '../../features/video-layout';
import JitsiManagement from './jitsi';
import senderHealth from './jitsi/senderHealth';
import MateManagement from './mate';
import { toggleLoad } from '../../features/base/loading/actions.ts';

let isLeaving = false;

class Management {
	constructor() {
		this.jitsi = new JitsiManagement();
		this.mateManagement = new MateManagement('');
		this.jitsiConference = null;
		this.leaveStatus = true;
	}

	setLeaveStatus(leaveStatus) {
		this.leaveStatus = leaveStatus;
	}

	/**
	 * 회의실 입장
	 */
	join(password, role_name, isTemp = false) {
		const { dispatch, getState } = APP.store;
		// [C1] 입장 내부 단계 타이밍(mate WS vs jitsi) — 배포 후 콘솔 확인용. 진단 완료 후 제거.
		const _c1Start = performance.now();
		const _c1 = label => console.log(`[C1입장-join] ${label} +${Math.round(performance.now() - _c1Start)}ms`);
		return new Promise((resolve, reject) => {
			this.mateManagement
				.connectAndJoin({ password, role_name }, null, isTemp)
				.then(result => {
					_c1('mate connectAndJoin');
					const user_id = getLocalIDMember(getState);
					this.jitsi
						.connectAndJoin(isTemp, user_id)
						.then(conference => {
							_c1('jitsi connectAndJoin');
							this.jitsiConference = conference;

							this.leaveStatus = false;
							isLeaving = false;
							return resolve();
						})
						.catch(err => {
							console.log(err);
							this.leave(err);
							reject(err);
						});
				})
				.catch(err => {
					this.leave(err);
					reject(err);
				});
		});
	}
	/**
	 * 회의실 퇴장
	 */
	async leave(alertMessage, isDupLogin = false) {
		// If the leave process is already running, return immediately
		if (isLeaving || this.leaveStatus) {
			return;
		}
		// 퇴장 사유를 서버 로그에 남긴다 — 강사가 "왜 나갔는지"(수동/중복로그인/오류)가 사고 타임라인의 핵심 단서
		senderHealth.noteLeave(isDupLogin ? 'dup-login' : alertMessage ? 'error' : 'user', alertMessage);
		
		// Mark the leave process as running
		isLeaving = true;
		this.leaveStatus = true;
		
		const individualLive = getPropertyValue(APP.store.getState, 'individualLive');
		const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
		
		try {
			if (getLocalSharingStatus(APP.store.getState)) {
				APP.store.dispatch(setLayoutMode(videoLayoutMode.default, false));
				await APP.management.setScreenOption(null, null);
				APP.management.stopScreenShare(false);
			}
			
			await Promise.all([
				this.mateManagement
				?.disconnect()
				.catch(err => console.error('Error disconnecting mateManagement:', err)),
				this.jitsi?.disconnect().catch(err => console.error('Error disconnecting jitsi:', err)),
			]);
			
			if (individualLive) {
				await APP.API.quitMeeting();
			}

			await APP.store.dispatch(setRoomJoined(false));
			await APP.store.dispatch(leaveRoom());

			if (isDupLogin) {
				await APP.eventManager.publish('UPDATE_PAGE', { page: PAGE_TYPE.DUP_LOGIN });
				return;
			} else {
				await APP.eventManager.publish('UPDATE_PAGE', { page: PAGE_TYPE.LOBBY });
				// alertMessage 는 사용자용 문자열만 표시한다. join 실패 등으로 Event/객체가 넘어오면
				// alert 에 "[object Event]" 로 뜨므로, 문자열이 아니면 로그만 남기고 표시하지 않는다.
				if (alertMessage && typeof alertMessage === 'string') {
					alert(alertMessage);
				} else if (alertMessage) {
					console.error('[leave] 비문자 alertMessage(원본):', alertMessage);
				}
				await sleep(100);
				window.close();
				return;
			}
		} catch (err) {
			console.error('Error during leave process:', err);
			// Event/객체가 그대로 넘어오면 "[object Event]" 로 표시되므로 문자열로 정규화.
			APP.UI.alertMessage(
				typeof err === 'string' ? err : (err?.message || '강의실에서 나가는 중 오류가 발생했습니다.'),
				true,
			);

			window.close();

			location.reload();
		}
	}

	toggleE2EE(isEnable) {
		this.jitsi.toggleE2EE(isEnable);
	}

	/**
	 * 전체 재접속(leave + rejoin). 이름과 달리 "미디어 세션만" 재시작하지 않는다 — 호출부(Video.tsx
	 * 프리즈 복구, senderHealth 3단계)가 그 비용(방 전체에 퇴장/입장·키프레임)을 알고 쓴다.
	 * 가벼운 세션 재시작은 restartMediaSessionsLight().
	 * @returns {Promise} 재접속 완료(회의 객체) — 동시 호출은 jitsi.reconnect 가 하나로 합친다
	 */
	restartMediaSessions() {
		return this.jitsi.reconnect().then(conference => {
			// 재접속 후 회의 객체가 바뀐다 — 종전에는 최초 join 때 잡은 참조가 그대로 남아
			// 재접속 뒤 이 참조를 쓰는 곳(참가자 조회 등)이 죽은 회의를 보고 있었다.
			if (conference) this.jitsiConference = conference;
			return conference;
		});
	}

	/** JVB 미디어 세션만 재시작(회의 유지, Jicofo 재초대). @returns {boolean} 호출 여부 */
	restartMediaSessionsLight() {
		return this.jitsi.restartMediaSessions();
	}

	conferenceAddedTrack(mediaType) {
		this.jitsi.conferenceAddedTrack(mediaType);
	}

	conferenceRemovedTrack(mediaType) {
		this.jitsi.conferenceRemovedTrack(mediaType);
	}

	conferenceMutedVideoTrack(isMuted) {
		if (APP.peerconnections && APP.peerconnections.length > 0) {
			APP.peerconnections.map(peerconnection => {
				peerconnection.muteVideo(isMuted);
			});
		}
	}

	conferenceReplaceTrack(newTrackId) {
		if (APP.peerconnections && APP.peerconnections.length > 0) {
			APP.peerconnections.map(peerconnection => {
				peerconnection.replaceTrack(newTrackId);
			});
		}
	}

	conferenceMutedVideoTrack(user_id, isMuted) {
		if (APP.peerconnections && APP.peerconnections.length > 0) {
			const peer = APP.peerconnections.find(peerconnection => peerconnection.user_id === user_id);

			if (peer) {
				peer.muteVideo(isMuted);
			}
		}
	}

	/**
	 * (TRACK) jitsi conference user_id를 mate에 업데이트 해준다.
	 * @param {string} bridge_id
	 */
	async setBridgeId(bridge) {
		const response = await this.mateManagement.setBridgeId(bridge);

		if (response.status !== 200) {
			/**
			 * (fix) 여기서의 에러 처리 필요
			 */
			console.log(' bridge id 세팅 오류 ');
		}
	}

	/**
	 * (LAYOUT) 레이아웃 변경
	 * @param {string} mode
	 * @returns
	 */
	async changeLayout(mode) {
		// 나의 방 권한이 존재하는 경우만 실행
		const viewVisible = _.cloneDeep(INIT_VISIBLE);
		if (VIDEO_MODE_LIST.indexOf(mode) !== -1) {
			// 레이아웃
			const response = await this.mateManagement.setLayoutMode(mode);
			if (response.status !== 200) {
				/**
				 * (fix) 여기서의 에러 처리 필요
				 */
				console.log(' 레이아웃 모드 오류 - ' + mode);
				return response;
			}
		} else {
			viewVisible[mode] = true;
		}

		// 화면 가시성
		return this.mateManagement.setVisibleLayoutMode(viewVisible);
	}

	/**
	 * (DOCUMENT) 문서 공유 저장 & 공유
	 * @param {Object} data
	 * @returns
	 */
	setShareItem(data) {
		return this.mateManagement.setShareItem(data);
	}
	/**
	 * (DOCUMENT) 문서 삭제
	 * @param {Object} data
	 * @returns
	 */
	deleteShareItem(data) {
		return this.mateManagement.deleteShareItem(data);
	}

	/**
	 * (DOCUMENT) 현재 문서 Foucs
	 * @param {string} uuid
	 * @param {Number} index
	 * @param {string} mode
	 * uuid, index: Number, mode
	 * @returns
	 */
	focusShareItem(uuid, index, mode) {
		return this.mateManagement.setFocusShareItem(uuid, index, mode);
	}

	/**
	 * (CANVAS) 문서 포인트 전송
	 * @param {string} uuid
	 * @param {Number} index
	 * @param {string} key
	 * @param {Object} property
	 * uuid, index: Number, key, property: Object
	 * @returns
	 */
	sendSharePoints(uuid, index, key, property) {
		return this.mateManagement.sendSharePoints(uuid, index, key, property);
	}

	/**
	 * (CANVAS) 문서 포인트 삭제 (라인 한줄 )
	 * @param {string} uuid
	 * @param {Number} index
	 * @param {string|null} key
	 * @param {boolean} isAll
	 * //uuid, index: Number, key|null, isAll
	 * @returns
	 */
	deleteShareLine(uuid, index, key, isAll) {
		return this.mateManagement.deleteShareLine(uuid, index, key, isAll);
	}
	/**
	 * (CANVAS) 문서 판서 삭제 ( 한페이지 )
	 * @param {string} uuid
	 * @param {Number} index
	 * @returns
	 */
	deleteShareCanvasIndex(uuid, index) {
		return this.mateManagement.deleteShareCanvasIndex(uuid, index);
	}

	/**
	 * (MEMBER) 발표자 권한 요청
	 * @param {string} member_uuid
	 * @returns
	 */
	requestPresentation(member_uuid) {
		return this.mateManagement.requestPresentation(member_uuid);
	}
	/**
	 * (MEMBER) 발표자 권한 응답
	 * @param {string} user_uuid
	 * @param {boolean} accepted
	 * @returns
	 */
	responsePresentation(user_uuid, accepted) {
		return this.mateManagement.responsePresentation(user_uuid, accepted);
	}

	/**
	 * (MEMBER) 사용자 권한 변경
	 * @param {string} user_uuid
	 * @param {string} role
	 * @param {string} group_id
	 * @returns
	 */
	setMemberRole(user_uuid, role) {
		return this.mateManagement.setMemberRole(user_uuid, role);
	}

	/**
	 * (MEMBER) 사용자 PIN 변경
	 * @param {string} user_uuid
	 * @param {boolean} pinned
	 * @returns
	 */
	setMemberPinned(user_uuid, pinned) {
		return this.mateManagement.setMemberPinned(user_uuid, pinned);
	}

	/**
	 * (MEMBER) 사용자 채팅 사용/해제
	 * @param {string} user_uuid
	 * @param {boolean} disabled
	 * @returns
	 */
	setMemberChatDisabled(user_uuid, disabled) {
		return this.mateManagement.setMemberChatDisabled(user_uuid, disabled);
	}

	/**
	 * (MEMBER) 사용자 강제 퇴장
	 * @param {string} user_uuid
	 * @returns
	 */
	setMemberKick(user_uuid) {
		return this.mateManagement.setMemberKick(user_uuid);
	}

	/**
	 * (MEMBER) 사용자 손들기
	 * @param {string} user_uuid
	 * @param {boolean} handler
	 * @returns
	 */
	raiseHand(user_uuid, handler) {
		return this.mateManagement.raiseHand(user_uuid, handler);
	}

	/**
	 * (TRACK) 비디오 ON / OFF
	 * @param {string} user_uuid
	 * @param {boolean} muted
	 * @returns
	 */
	setVideo(user_uuid, muted) {
		return this.mateManagement.setVideo(user_uuid, muted);
	}

	/**
	 * (TRACK) 오디오 ON / OFF
	 * @param {string} user_uuid
	 * @param {boolean} muted
	 * @returns
	 */
	setAudio(user_uuid, muted) {
		return this.mateManagement.setAudio(user_uuid, muted);
	}

	/**
	 * (TRACK) 전체 비디오 ON / OFF
	 * @param {boolean} muted
	 * @returns
	 */
	setAllVideo(muted) {
		return this.mateManagement.setAllVideo(muted);
	}

	/**
	 * (TRACK) 전체 오디오 ON / OFF
	 * @param {boolean} muted
	 * @returns
	 */
	setAllAudio(muted) {
		return this.mateManagement.setAllAudio(muted);
	}

	/**
	 * (CHAT) 채팅 전송
	 * @param {objcet} data
	 */
	sendMessage(data) {
		return this.mateManagement.sendMessage(data);
	}
	/**
	 * (CHAT) 파일 전송
	 * @param {Objcet} data
	 */
	sendFile(data) {
		return this.mateManagement.sendFile(data);
	}

	/**
	 * (SCREEN SHARE)
	 * 화면 녹화 시작
	 * @returns
	 */
	startScreenShare() {
		return this.jitsi.startScreenShare();
	}
	/**
	 * (SCREEN SHARE)
	 * 화면 녹화 종료
	 * @returns
	 */
	stopScreenShare() {
		return this.jitsi.stopScreenShare();
	}

	/**
	 * (SCREEN SHARE)
	 * 현재 화면 공유 중이거나 화면 공유 종료를 알림.
	 * @param {string} jitsi_id
	 * @param {string} member_uuid
	 * @returns
	 */
	setScreenOption(jitsi_id, member_uuid) {
		return this.mateManagement.setScreenOption(jitsi_id, member_uuid);
	}

	/**
	 * (POLICY)
	 * 현재 권한
	 * @param {Object} policy
	 * @returns
	 */
	updateRoomPolicy(policy) {
		return this.mateManagement.updateRoomPolicy({ policy });
	}

	/**
	 * (POLICY)
	 * 그리드 수 변경
	 * @param {Number} count
	 */
	setGridCount(count) {
		return this.mateManagement.setGridCount(count);
	}

	/**
	 * (MEMBER) 녹화 중을 알림.
	 * @param {*} user_uuid
	 * @param {*} recording
	 * @returns
	 */
	setRecording(user_uuid, recording) {
		return this.mateManagement.setRecording(user_uuid, recording);
	}

	/**
	 * (TRACK)
	 * @param {string} newTrackId
	 * @param {string} type
	 * @param {object} options
	 */
	updateTrack(newTrackId, type, options) {
		// jitsi.updateTrack 은 실패 시 내부에서 로그 후 rethrow 한다 — 호출부(미들웨어)가 await 하지
		// 않는 fire-and-forget 경로이므로 여기서 catch 를 붙여 unhandled rejection 소음만 막는다.
		return this.jitsi.updateTrack(newTrackId, type, options).catch(() => {});
	}

	/**
	 * (TRACK)
	 * @param {*} oldTrack
	 * @param {*} newTrack
	 */
	replaceTrack(oldTrack, newTrack) {
		return this.jitsi.replaceTrack(oldTrack, newTrack);
	}

	setConstraints(data) {
		return this.jitsi.setConstraints(data);
	}

	/**
	 * 로컬 사용자의 연결 상태 이벤트 등록
	 * @param {function} handler
	 */
	registerUpdateLocalStats(handler) {
		this.jitsi.registerUpdateLocalStats(handler);
	}
	/**
	 * remote 사용자의 연결 상태 이벤트 등록
	 * @param {function} handler
	 */
	registerUpdateRemoteStats(handler) {
		this.jitsi.registerUpdateRemoteStats(handler);
	}

	/**
	 * 전체 상태 업데이트
	 * @param {boolean} running
	 * @returns
	 */
	updateGroupStatus(running) {
		return this.mateManagement.updateGroupStatus(running);
	}

	/**
	 * 그룹에 알림 메시지 전달
	 * @param {string} message: 전달할 메시지
	 * @param {string} group_id: 그룹 아이디
	 */
	notifyGroupMessage(message, group_id) {
		return this.mateManagement.notifyGroupMessage(message, group_id);
	}

	/**
	 * (SURVEY) 설문조사 알림
	 * @returns
	 */
	notifySurvey(uuid) {
		return this.mateManagement.notifySurvey(uuid);
	}
	/**
	 * (SURVEY) 설문조사 응답
	 * @param { string } uuid
	 * @returns
	 */
	replySurvey(uuid) {
		return this.mateManagement.replySurvey(uuid);
	}

	updateNickname(nickname) {
		return this.mateManagement.updateNickname(nickname);
	}

	/**
	 * Audio Output
	 */
	getAudioOutputDeviceId() {
		return this.jitsi.getAudioOutputDeviceId();
	}
	setAudioOutputDevice(newId) {
		return this.jitsi.setAudioOutputDevice(newId);
	}

	callOfferMember(offer) {
		return this.mateManagement.callOfferMember(offer);
	}

	callAnswerMember(answer) {
		return this.mateManagement.callAnswerMember(answer);
	}

	callCandidateMember(candidate) {
		return this.mateManagement.callCandidateMember(candidate);
	}
}

export default Management;
